import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { clsx } from 'clsx';
import { SvgCloseSearch } from '@/components/icons';
import ArrowSvg from '@/assets/icons/arrow.svg';
import { IWithClassname } from '@/models/common';
import styles from '@/components/features/Dropdown/Dropdown.module.scss';

interface Props<Option> extends IWithClassname {
  fieldName: string;
  placeholder?: string;
  options?: Option[];
}

export const ControlledDropdown = <Option extends { id: number | string; name: string }, >(props: Props<Option>) => {
  const {
    fieldName,
    placeholder = 'Выберите',
    options = [],
    className,
  } = props;

  const { control, resetField } = useFormContext();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    document.addEventListener('click', closeDropdown);

    return () => document.removeEventListener('click', closeDropdown);
  }, []);

  const toggleDropdown = () => setIsOpen(!isOpen);

  const closeDropdown = (e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsOpen(false);
    }
  };

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <div ref={dropdownRef} className={clsx(styles.dropdown, className)}>
          <div
            className={clsx(styles.dropdown__head, { [styles.dropdown__head_error]: error })}
            onClick={toggleDropdown}
          >
            {value ? (
              <>
                <div>{value.name}</div>
                <button className={styles.dropdown__cross} type="button" onClick={() => resetField(fieldName)}>
                  <SvgCloseSearch />
                </button>
              </>
            ) : (
              <>
                <div className={styles.dropdown__placeholder}>{placeholder}</div>
                <ArrowSvg className={clsx(styles.dropdown__arrow, { [styles.dropdown__arrow_reverse]: isOpen })} />
              </>
            )}
          </div>

          <div className={clsx(styles.dropdown__listWrapper, { [styles.dropdown__listWrapper_opened]: isOpen })}>
            <ul className={styles.dropdown__list}>
              {options.map((option: Option) => {
                return (
                  <li
                    key={option.id}
                    className={styles.dropdown__item}
                    onClick={() => {
                      onChange(option);
                      setIsOpen(false);
                    }}
                  >
                    {option.name}
                  </li>
                );
              })}
            </ul>
          </div>

          <span className={clsx(styles.dropdown__errorHint, { [styles.dropdown__errorHint_visible]: error })}>
            {error?.message}
          </span>
        </div>
      )}
    />
  );
};